/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Tab component for UserRole
 *
 * @file UserRoleTab.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import ResourceUtils from '../../utils/ResourceUtils';
import UserRoleStyledTab from './UserRoleStyledTab';
import UserRoleStyledTabs from './UserRoleStyledTabs';
import messages from '../../../translations/defaultMessages';

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    margin: 0
  }
});

const a11yProps = (index) => ({
  id: `user-role-tab-${index}`,
  'aria-controls': `user-role-tabpanel-${index}`
});

const UserRoleTab = ({
  bulkDownloadStatus,
  handleTabChange,
  intl,
  tabValue,
  showTeacherTab,
  showStudentTab
}) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(tabValue);
  const isBulkDownloadInProgress = ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus);
  const { formatMessage } = intl;

  /**
   * Handle user role tab change
   * @param {*} event
   * @param {*} currentValue
   */
  const handleChange = (event, currentValue) => {
    handleTabChange(currentValue);
    setValue(currentValue);
  };

  return (
    <div className={classes.root}>
      <UserRoleStyledTabs
        aria-label="User Role"
        indicatorColor="primary"
        onChange={handleChange}
        textColor="primary"
        value={value}
        variant="fullWidth"
      >
        <UserRoleStyledTab
          disabled={!showStudentTab || isBulkDownloadInProgress}
          label={formatMessage(messages.student)}
          {...a11yProps(0)}
        />
        <UserRoleStyledTab
          disabled={!showTeacherTab || isBulkDownloadInProgress}
          label={formatMessage(messages.teacher)}
          {...a11yProps(1)}
        />
      </UserRoleStyledTabs>
    </div>
  );
};

UserRoleTab.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  intl: intlShape,
  tabValue: PropTypes.number.isRequired,
  showTeacherTab: PropTypes.bool.isRequired,
  showStudentTab: PropTypes.bool.isRequired
};

UserRoleTab.defaultProps = {
  intl: {}
};

export default injectIntl(UserRoleTab);
